import {
  contrastRatio,
  meetsContrast,
  MIN_BOUNDARY_CONTRAST,
  MIN_TEXT_CONTRAST,
  pickMostReadable,
} from './contrast';
import {
  DEFAULT_THEME,
  FOCUS_RING_FALLBACK,
  NEAR_BLACK,
  NEAR_WHITE,
  PURE_BLACK,
  PURE_WHITE,
  type Scheme,
  STATUS_COLORS,
  type Theme,
} from './defaults';
import { isValidHex, mix, normalizeHex } from './hex';

/**
 * Steps 5–7 of the application order in `docs/Branding-And-Theming.md` §9: validate, enforce
 * contrast, derive. All pure and DOM-free, which is why they can be tested exhaustively — only
 * `applyBranding` touches the document.
 */

export interface ThemeNote {
  token: string;
  reason: 'invalid' | 'contrast';
  /** Never a raw merchant string in a rendered surface; this is DEV-log material only. */
  rejected: string;
  replacement: string;
  ratio?: number | undefined;
}

export interface ValidatedTheme {
  theme: Theme;
  notes: ThemeNote[];
}

const THEME_KEYS = ['primary', 'secondary', 'background', 'text'] as const;

/**
 * Replaces any value that is not a literal `#RRGGBB` with the scheme default.
 *
 * Falls back per token rather than rejecting the whole palette: a merchant with three good colors
 * and one typo should still look like themselves.
 */
export function validateTheme(raw: unknown, scheme: Scheme): ValidatedTheme {
  const fallback = DEFAULT_THEME[scheme];
  const source = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const notes: ThemeNote[] = [];
  const theme: Theme = { ...fallback };

  for (const key of THEME_KEYS) {
    const value = source[key];
    if (isValidHex(value)) {
      theme[key] = normalizeHex(value);
    } else {
      notes.push({
        token: key,
        reason: 'invalid',
        rejected: typeof value === 'string' ? value : String(value),
        replacement: fallback[key],
      });
    }
  }

  return { theme, notes };
}

/**
 * Overrides foregrounds until the palette is readable. Never touches `background`.
 *
 * The background is the most recognisable part of a brand and the text color is the part that must
 * be legible, so overriding `text` preserves both identity and readability. Overriding `background`
 * would visibly break the merchant's page for the sake of a rule the player cannot see.
 */
export function enforceContrast(theme: Theme): ValidatedTheme {
  const notes: ThemeNote[] = [];
  const result: Theme = { ...theme };

  if (!meetsContrast(result.text, result.background, MIN_TEXT_CONTRAST)) {
    const replacement = pickMostReadable(result.background, [NEAR_BLACK, NEAR_WHITE]);
    notes.push({
      token: 'text',
      reason: 'contrast',
      rejected: result.text,
      replacement,
      ratio: Number(contrastRatio(result.text, result.background).toFixed(2)),
    });
    result.text = replacement;
  }

  return { theme: result, notes };
}

export interface DerivedTokens {
  vars: Record<string, string>;
  notes: ThemeNote[];
}

/**
 * The contract supplies four colors; a usable interface needs about a dozen. Everything else is
 * derived deterministically so that a merchant supplying only a primary still gets a coherent,
 * accessible surface set rather than a half-styled page.
 */
export function deriveTokens(theme: Theme, scheme: Scheme): DerivedTokens {
  const notes: ThemeNote[] = [];
  const { primary, secondary, background, text } = theme;

  // Hover and active shift toward black in light schemes and white in dark ones, so the control
  // always moves *away* from the page rather than becoming less visible against it.
  const shiftTarget = scheme === 'light' ? PURE_BLACK : PURE_WHITE;

  const surface = mix(background, text, 0.03);
  const surfaceMuted = mix(background, text, 0.06);

  const onPrimary = pickMostReadable(primary, [PURE_WHITE, PURE_BLACK]);

  // Step back toward `text` until the muted variant is readable on the surface it sits on. Muted
  // text carries real content here — help text under the UTR field, the "what happens next" copy on
  // an outcome screen — so it is held to the full 4.5:1 rather than a relaxed bar.
  let textMuted = mix(text, background, 0.38);
  for (const ratio of [0.3, 0.22, 0.14, 0.06, 0]) {
    if (meetsContrast(textMuted, surface, MIN_TEXT_CONTRAST)) break;
    textMuted = mix(text, background, ratio);
  }
  if (!meetsContrast(textMuted, surface, MIN_TEXT_CONTRAST)) {
    notes.push({
      token: 'text-muted',
      reason: 'contrast',
      rejected: textMuted,
      replacement: text,
      ratio: Number(contrastRatio(textMuted, surface).toFixed(2)),
    });
    textMuted = text;
  }

  // Quiet chrome only. Pushing every divider to 3:1 in dark mode produces a grey cage around the
  // page. Interactive outlines use `--vp-border-strong`; selected rows use `--vp-primary-soft`.
  const border = mix(background, text, 0.14);

  let borderStrong = mix(background, text, 0.22);
  for (const ratio of [0.32, 0.45, 0.6]) {
    if (meetsContrast(borderStrong, surface, MIN_BOUNDARY_CONTRAST)) break;
    borderStrong = mix(background, text, ratio);
  }

  const primarySoft = mix(background, primary, scheme === 'dark' ? 0.16 : 0.12);
  const secondarySoft = mix(background, secondary, scheme === 'dark' ? 0.16 : 0.12);

  // Text and icons sit on `background` / `surface`, never on a filled secondary control. Keep the
  // contract hex on `--vp-secondary` for identity; `--vp-secondary-fg` is what the UI actually paints.
  let secondaryFg = secondary;
  const secondaryReadable =
    meetsContrast(secondaryFg, background, MIN_TEXT_CONTRAST) &&
    meetsContrast(secondaryFg, surface, MIN_TEXT_CONTRAST);
  if (!secondaryReadable) {
    for (const ratio of [0.15, 0.3, 0.45, 0.6, 0.8, 1]) {
      secondaryFg = mix(secondary, text, ratio);
      if (
        meetsContrast(secondaryFg, background, MIN_TEXT_CONTRAST) &&
        meetsContrast(secondaryFg, surface, MIN_TEXT_CONTRAST)
      ) {
        break;
      }
    }
    if (
      !meetsContrast(secondaryFg, background, MIN_TEXT_CONTRAST) ||
      !meetsContrast(secondaryFg, surface, MIN_TEXT_CONTRAST)
    ) {
      notes.push({
        token: 'secondary-fg',
        reason: 'contrast',
        rejected: secondary,
        replacement: text,
        ratio: Number(
          Math.min(contrastRatio(secondary, background), contrastRatio(secondary, surface)).toFixed(
            2,
          ),
        ),
      });
      secondaryFg = text;
    } else if (secondaryFg !== secondary) {
      notes.push({
        token: 'secondary-fg',
        reason: 'contrast',
        rejected: secondary,
        replacement: secondaryFg,
        ratio: Number(
          Math.min(contrastRatio(secondary, background), contrastRatio(secondary, surface)).toFixed(
            2,
          ),
        ),
      });
    }
  }

  const focusRingCandidate = primary;
  const focusRing =
    meetsContrast(focusRingCandidate, background, MIN_BOUNDARY_CONTRAST) &&
    meetsContrast(focusRingCandidate, surface, MIN_BOUNDARY_CONTRAST)
      ? focusRingCandidate
      : FOCUS_RING_FALLBACK[scheme];

  if (focusRing !== focusRingCandidate) {
    notes.push({
      token: 'focus-ring',
      reason: 'contrast',
      rejected: focusRingCandidate,
      replacement: focusRing,
      // The ring has to clear 3:1 against the page *and* against a card, so the lower of the two is
      // the number that actually failed. Logging only the background ratio produced a note showing
      // a passing value next to a substitution, which reads as a bug in the enforcement.
      ratio: Number(
        Math.min(
          contrastRatio(focusRingCandidate, background),
          contrastRatio(focusRingCandidate, surface),
        ).toFixed(2),
      ),
    });
  }

  const status = STATUS_COLORS[scheme];

  return {
    notes,
    vars: {
      '--vp-primary': primary,
      '--vp-primary-hover': mix(primary, shiftTarget, 0.08),
      '--vp-primary-active': mix(primary, shiftTarget, 0.16),
      '--vp-on-primary': onPrimary,
      '--vp-secondary': secondary,
      '--vp-secondary-fg': secondaryFg,
      '--vp-secondary-soft': secondarySoft,
      '--vp-background': background,
      '--vp-surface': surface,
      '--vp-surface-muted': surfaceMuted,
      '--vp-text': text,
      '--vp-text-muted': textMuted,
      '--vp-border': border,
      '--vp-border-strong': borderStrong,
      '--vp-primary-soft': primarySoft,
      '--vp-focus-ring': focusRing,
      '--vp-success': status.success,
      '--vp-warning': status.warning,
      '--vp-danger': status.danger,
      '--vp-info': status.info,
    },
  };
}

/** Convenience for the full pure pipeline: validate → enforce → derive. */
export function resolveTokens(rawTheme: unknown, scheme: Scheme): DerivedTokens {
  const validated = validateTheme(rawTheme, scheme);
  const enforced = enforceContrast(validated.theme);
  const derived = deriveTokens(enforced.theme, scheme);
  return {
    vars: derived.vars,
    notes: [...validated.notes, ...enforced.notes, ...derived.notes],
  };
}
